import { ACTION_TYPE } from "../core/redux/actionType";
import {
  hasTrippedBreaker,
  updateBreaker,
} from "../core/redux/api/circuitBreaker/module";
import { apiBulkDeleteLiftAccessRules } from "../core/redux/api/endpoints";
import {
  addError,
  addLoading,
  removeLoadingAndError,
} from "../core/redux/api/module";
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from "../core/redux/api/reduxStandardApiAction";
import { IAppState } from "../core/redux/IAppState";
import { AccessRuleMultiContextDeleteRequest } from "./accessRules/requests/accessRuleRequestBuilder";
import {
  entitlementContextKey,
  liftAccessRuleSetKey,
  toEntitlementContext,
} from "./accessRules/liftAccessRuleSet/models";
import { LiftAccessRuleBatchSaveResult } from "./models";

export const KEY_ACTION = ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_REQUEST;

export const ACTIONS = [
  ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_REQUEST,
  ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_RESPONSE,
  ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_ERROR,
];

/**
 * Deletes every lift access rule for a set of entitlement contexts in one set-based
 * call, returning the outcome per context.
 *
 * A bulk run sends many contexts, a single rule set delete exactly one. The reducer
 * below prunes only the contexts that succeeded — pruning every requested context
 * would make a rule set that failed to delete vanish from the results list.
 */
export const bulkDeleteLiftAccessRuleSetsAction = (
  deleteRequest: AccessRuleMultiContextDeleteRequest,
) => {
  return makeApiAction({
    endpoint: apiBulkDeleteLiftAccessRules(),
    method: "POST",
    body: JSON.stringify(deleteRequest),

    headers: (state: IAppState) => ({
      "content-type": "application/json",
      Authorization: `Bearer ${state.auth.bearerToken}`,
    }),

    types: ACTIONS.map((type) => ({
      type,
      meta: { deleteRequest },
    })) as RsaaTypes,

    bailout: (state: IAppState) => hasTrippedBreaker(state, KEY_ACTION),
  });
};

const reducer: ApiReducer<LiftAccessRuleBatchSaveResult> = (
  state,
  action,
  error,
  isLoading,
) => {
  if (isLoading) {
    return addLoading(state, KEY_ACTION, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  // A 400 still carries per-context results, so record the error but read on.
  const base = error
    ? addError(withBreaker, KEY_ACTION, KEY_ACTION, error)
    : removeLoadingAndError(withBreaker, KEY_ACTION, KEY_ACTION);

  // Prune only what the API says was deleted. On an HTTP error the parsed body sits
  // under payload.response; on a 2xx it is the payload.
  const payload: any = (action as any)?.payload;
  const body = error ? payload?.response : payload;
  const results: any[] = Array.isArray(body?.results)
    ? body.results
    : Array.isArray(body?.Results)
      ? body.Results
      : [];

  const deletedKeys = new Set(
    results
      .filter((r) => (r?.success ?? r?.Success) === true)
      .map((r) => toEntitlementContext(r?.context ?? r?.Context))
      .filter((c): c is NonNullable<typeof c> => !!c)
      .map(entitlementContextKey),
  );

  if (deletedKeys.size === 0 || !state.liftAccess?.liftAccessRuleSets) {
    return base;
  }

  const updatedRuleSets = state.liftAccess.liftAccessRuleSets.filter(
    (rule) => !deletedKeys.has(liftAccessRuleSetKey(rule)),
  );

  return {
    ...base,
    liftAccess: {
      ...state.liftAccess,
      liftAccessRuleSets: updatedRuleSets,
    },
  };
};

export const bulkDeleteLiftAccessRulesReducer =
  makeApiReducer<LiftAccessRuleBatchSaveResult>(ACTIONS, KEY_ACTION, reducer);
